import type { PoolGame, Ball } from "./types.js";
import { TABLE_W, TABLE_H, RAIL, BALL_R, POCKETS, POCKET_R } from "./types.js";

function initBalls(): Ball[] {
  const balls: Ball[] = [];
  const startX = TABLE_W * 0.65;
  const startY = TABLE_H / 2;
  const spacing = BALL_R * 2 + 1;
  const rows = [
    [1],
    [9, 2],
    [3, 10, 8],
    [11, 4, 14, 5],
    [6, 12, 15, 13, 7],
  ];
  for (let row = 0; row < rows.length; row++) {
    for (let col = 0; col < rows[row].length; col++) {
      const x = startX + row * spacing * Math.cos(Math.PI / 6);
      const y = startY + (col - (rows[row].length - 1) / 2) * spacing;
      balls.push({ x, y, vx: 0, vy: 0, number: rows[row][col], pocketed: false });
    }
  }
  return balls;
}

export function createGame(): PoolGame {
  return {
    balls: initBalls(),
    cueBall: { x: TABLE_W * 0.25, y: TABLE_H / 2, vx: 0, vy: 0, number: 0, pocketed: false },
    aimAngle: 0,
    power: 30,
    turn: 1,
    inMotion: false,
    foul: false,
    winner: null,
    message: "Player 1's turn",
    placed: true,
  };
}

export function setAim(game: PoolGame, angle: number): PoolGame {
  if (game.inMotion) return game;
  return { ...game, aimAngle: angle };
}

export function setPower(game: PoolGame, power: number): PoolGame {
  if (game.inMotion) return game;
  return { ...game, power: Math.max(5, Math.min(100, power)) };
}

export function shoot(game: PoolGame): PoolGame {
  if (game.inMotion || game.winner !== null) return game;
  const angleRad = (game.aimAngle * Math.PI) / 180;
  const speed = game.power * 3;
  return {
    ...game,
    cueBall: { ...game.cueBall, vx: Math.cos(angleRad) * speed, vy: Math.sin(angleRad) * speed },
    inMotion: true,
    foul: false,
  };
}

function isInPocket(d: { x: number; y: number }): boolean {
  for (const p of POCKETS) {
    const dx = d.x - p.x;
    const dy = d.y - p.y;
    if (Math.sqrt(dx * dx + dy * dy) < POCKET_R) return true;
  }
  return false;
}

function resolveCollision(a: Ball, b: Ball): { a: Ball; b: Ball } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist === 0) return { a, b };
  const overlap = BALL_R * 2 - dist;
  if (overlap <= 0) return { a, b };

  const nx = dx / dist;
  const ny = dy / dist;

  const dvx = a.vx - b.vx;
  const dvy = a.vy - b.vy;
  const dvn = dvx * nx + dvy * ny;
  if (dvn > 0) return { a, b };

  const ma = 1, mb = 1;
  const impulse = (2 * dvn) / (ma + mb);

  return {
    a: { ...a, vx: a.vx - impulse * mb * nx, vy: a.vy - impulse * mb * ny },
    b: { ...b, vx: b.vx + impulse * ma * nx, vy: b.vy + impulse * ma * ny },
  };
}

export function tick(game: PoolGame, dt: number): PoolGame {
  if (!game.inMotion) return game;
  let dtSec = dt / 1000;
  if (dtSec > 0.05) dtSec = 0.05;

  const balls = game.balls.map((b) => ({
    ...b, vx: b.vx * 0.985, vy: b.vy * 0.985,
    x: b.x + b.vx * dtSec, y: b.y + b.vy * dtSec,
  }));
  let cueBall = {
    ...game.cueBall, vx: game.cueBall.vx * 0.985, vy: game.cueBall.vy * 0.985,
    x: game.cueBall.x + game.cueBall.vx * dtSec,
    y: game.cueBall.y + game.cueBall.vy * dtSec,
  };

  const allBalls: Ball[] = [cueBall, ...balls];

  for (const b of allBalls) {
    if (b.pocketed) continue;
    if (b.x - BALL_R < RAIL) { b.x = RAIL + BALL_R; b.vx = Math.abs(b.vx) * -0.5; }
    if (b.x + BALL_R > TABLE_W - RAIL) { b.x = TABLE_W - RAIL - BALL_R; b.vx = -Math.abs(b.vx) * 0.5; }
    if (b.y - BALL_R < RAIL) { b.y = RAIL + BALL_R; b.vy = Math.abs(b.vy) * -0.5; }
    if (b.y + BALL_R > TABLE_H - RAIL) { b.y = TABLE_H - RAIL - BALL_R; b.vy = -Math.abs(b.vy) * 0.5; }

    if (isInPocket(b)) {
      b.pocketed = true;
      b.vx = 0; b.vy = 0;
    }
  }

  for (let i = 0; i < allBalls.length; i++) {
    for (let j = i + 1; j < allBalls.length; j++) {
      if (allBalls[i].pocketed || allBalls[j].pocketed) continue;
      const result = resolveCollision(allBalls[i], allBalls[j]);
      allBalls[i] = result.a;
      allBalls[j] = result.b;
    }
  }

  cueBall = allBalls[0];
  const updatedBalls = allBalls.slice(1) as Ball[];

  const anyMotion = allBalls.some((b) => !b.pocketed && (Math.abs(b.vx) > 0.3 || Math.abs(b.vy) > 0.3));

  if (!anyMotion) {
    const cuePocketed = cueBall.pocketed;
    let pocketed8 = false;
    let pocketedCount = 0;
    for (const b of updatedBalls) {
      if (b.pocketed && b.number === 8) pocketed8 = true;
      if (b.pocketed) pocketedCount++;
    }

    let foul = cuePocketed;
    let nextTurn: 1 | 2 = game.turn === 1 ? 2 : 1;
    let winner: number | null = null;

    if (pocketed8) {
      winner = foul ? nextTurn : game.turn;
    }

    return {
      ...game,
      balls: updatedBalls,
      cueBall: cuePocketed
        ? { x: TABLE_W * 0.25, y: TABLE_H / 2, vx: 0, vy: 0, number: 0, pocketed: false }
        : { ...cueBall, vx: 0, vy: 0 },
      inMotion: false,
      foul,
      turn: foul || pocketedCount === 0 ? nextTurn : game.turn,
      winner,
      message: winner ? `Player ${winner} wins!` : foul ? `Player ${nextTurn}'s turn (foul)` : `Player ${game.turn}'s turn`,
    };
  }

  return { ...game, balls: updatedBalls, cueBall, inMotion: true };
}
